import { Fragment, type MouseEvent, type ReactNode, useContext, useLayoutEffect, useRef } from 'react'
import { Link, UNSAFE_DataRouterStateContext, useLocation } from 'react-router'

import { TOP_BAR_TAB_CLASS, TOP_BAR_TAB_DIVIDER_CLASS, TOP_BAR_TABS_CLASS } from './topBarTabs'

export interface ProductNavLink {
  label: string
  /** A route in this app, where the tab is current on it and every page under it, or another app's URL. */
  to: string
  /** Where the tab is current, when that is not just `to`: the console lives under Insurance's path. */
  section?: string
  /** A path under the tab's section that belongs to another tab. */
  except?: string
  /** Set apart from the products after it by a rule, as Cai is. */
  lead?: boolean
  /** Set apart from the products before it by a rule, as the staff console is. */
  apart?: boolean
  /** Runs before the move; preventDefault holds it, e.g. to finish a transition first. */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void
  /** Starts loading the tab's page as the pointer or focus reaches it: the router waits for that code. */
  prefetch?: () => void
  /** Shown after the label, such as a count of what waits there. */
  badge?: ReactNode
}

function isUnder(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`)
}

/** The shared shell's products as line tabs that navigate in place, without loading the page again. */
export function ProductNavLinks({ links, neutral = [] }: { links: ProductNavLink[]; neutral?: string[] }) {
  const location = useLocation()
  // The tab moves with the click, not once the product it opens has loaded; a plain router has no pending move.
  const navigation = useContext(UNSAFE_DataRouterStateContext)?.navigation
  const pathname = navigation?.state === 'loading' ? navigation.location.pathname : location.pathname
  // Pages that belong to no product, such as the account's settings, keep the tab the browser came from.
  const onNeutral = neutral.some((path) => isUnder(pathname, path))
  const lastPathname = useRef(pathname)
  useLayoutEffect(() => {
    if (!onNeutral) lastPathname.current = pathname
  }, [onNeutral, pathname])
  const tabPathname = onNeutral ? lastPathname.current : pathname
  return (
    <nav aria-label="Cai products" className="h-full">
      <div className={TOP_BAR_TABS_CLASS}>
        {links.map((link) => {
          const current =
            isUnder(tabPathname, link.section ?? link.to) &&
            !(link.except && isUnder(tabPathname, link.except))
          return (
            <Fragment key={link.to}>
              {link.apart && <span aria-hidden="true" className={TOP_BAR_TAB_DIVIDER_CLASS} />}
              {link.to.startsWith('/') ? (
                <Link
                  to={link.to}
                  aria-current={current ? 'page' : undefined}
                  data-state={current ? 'active' : 'inactive'}
                  className={TOP_BAR_TAB_CLASS}
                  onClick={link.onClick}
                  onPointerEnter={link.prefetch}
                  onFocus={link.prefetch}
                  onTouchStart={link.prefetch}
                >
                  {link.label}
                  {link.badge}
                </Link>
              ) : (
                <a href={link.to} data-state="inactive" className={TOP_BAR_TAB_CLASS}>
                  {link.label}
                </a>
              )}
              {link.lead && <span aria-hidden="true" className={TOP_BAR_TAB_DIVIDER_CLASS} />}
            </Fragment>
          )
        })}
      </div>
    </nav>
  )
}
